import { Box, Theme } from "@outschool/backpack";
import { Container, Loading } from "@outschool/ui-legacy-component-library";
import { lazyWithRetry } from "@outschool/ui-utils";
import React, { useEffect, useState } from "react";

import DesktopActivityCard from "../DesktopActivityCard";
import useTutoringFlow from "../hooks/useTutoringFlow";
import { ReviewsSection } from "../sections/Reviews";
import TeacherSection from "../sections/TeacherSection";
import ClassDetailsNav from "../subcomponents/ClassDetailsNav";
import DesktopActivityHeader from "../subcomponents/DesktopActivityHeader";
import { DesktopClassDetailsMainBodyProps } from "./Components";

const WhatsIncluded = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "whats-included" */ "../sections/WhatsIncluded")
);
const ActivityDetailDescription = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "activity-detail-description" */ "../sections/ActivityDetailDescription"
    )
);
const LearningGoals = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "learning-goals" */ "../sections/LearningGoals")
);
const SyllabusSummary = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "syllabus-summary" */ "../sections/SyllabusSummary"
    )
);
const OtherDetails = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "other-details" */ "../sections/OtherDetails")
);
const Disclaimer = lazyWithRetry(
  () => import(/* webpackChunkName: "disclaimer" */ "../sections/Disclaimer")
);
const ClubExplanationSection = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "club-explanation" */ "../sections/ClubExplanationSection"
    )
);

export default function DesktopClassDetailsMainBody({
  // props
  activity,
  activityWithEnrollableTimes,
  classNavTabs,
  hasOneOnOneAvailabilities,
  isLoadingOneOnOneAvailabilities,
  isOrganizationEnrollment,
  leader,
  onClickMoreLikeThis,
  onClickViewReviews,
  onClickViewTeacher,
  setIsAvailabilityModalOpen,
  setTabValue,
  showAvailableTimes,
  tabValue,
  // refs
  refsContainer,
  sectionsRef,
  viewAvailabilityRef,
  // memos & elements
  ActivityEnrollableTimes,
  AvailableTimes
}: DesktopClassDetailsMainBodyProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const { tutoringShowAvailableTimesInline } = useTutoringFlow();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  ActivityEnrollableTimes = ActivityEnrollableTimes ?? <></>;
  AvailableTimes = AvailableTimes ?? <></>;

  const AvailableTimesView =
    isLoaded && showAvailableTimes ? (
      <Box ref={sectionsRef}>{AvailableTimes}</Box>
    ) : null;

  return (
    <Container
      sx={{
        display: "flex",
        marginY: "40px",
        alignItems: "flex-start"
      }}
    >
      <Box
        flex
        sx={(theme: Theme) => ({
          marginRight: "3em",
          maxWidth: "76rem",
          minWidth: "0", // override automatic minimum width
          flexDirection: "column",
          [theme.breakpoints.down("lg")]: {
            marginRight: "1.5em"
          }
        })}
        ref={refsContainer.root}
      >
        <Box ref={refsContainer.about.header}>
          <DesktopActivityHeader
            activity={activity}
            leader={leader}
            onClickViewTeacher={onClickViewTeacher}
            onClickViewReviews={onClickViewReviews}
          />
        </Box>
        <ClassDetailsNav tabs={classNavTabs ?? []} />
        <Box
          flex
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: "3.2rem",
            gap: "9.6rem"
          }}
        >
          {!isLoaded ? (
            <Loading fullHeight />
          ) : (
            <>
              <WhatsIncluded
                activity={activity}
                headerRef={refsContainer.about.whatsIncluded}
              />
              <ActivityDetailDescription
                activity={activity}
                headerRef={refsContainer.about.classExperience}
              />
              <LearningGoals
                learningGoals={activity.learningGoals}
                headerRef={refsContainer.learningOverview.learningGoals}
              />
              <SyllabusSummary
                activity={activity}
                headerRef={refsContainer.learningOverview.syllabus}
              />
              <OtherDetails
                activity={activity}
                headerRef={refsContainer.learningOverview.otherDetails}
              />
              {activity.disclaimer && <Disclaimer text={activity.disclaimer} />}
              {AvailableTimesView}

              {/* // If tutoringShowAvailableTimesInline is true, we show ActivityEnrollableTimes inline here.
              // Otherwise, the available times are shown in the enrollment modal instead. */}
              {!!hasOneOnOneAvailabilities &&
                activity.canEnroll &&
                tutoringShowAvailableTimesInline && (
                  <Box ref={viewAvailabilityRef} sx={{ paddingY: 96 }}>
                    {ActivityEnrollableTimes}
                  </Box>
                )}
              {activity.isClub && (
                <ClubExplanationSection
                  activity={activity}
                  isOrganizationEnrollment={isOrganizationEnrollment}
                />
              )}
              <TeacherSection
                activity={activity}
                leader={leader}
                onClickViewReviews={onClickViewReviews}
                teacherHeaderRef={refsContainer.teacher.header}
                credentialsRef={refsContainer.teacher.expertise}
              />
              {!!tabValue && !!setTabValue && (
                <ReviewsSection
                  activity={activity}
                  reviewsRef={refsContainer.reviews}
                  setTabValue={setTabValue}
                  tabValue={tabValue}
                />
              )}
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ position: "sticky", top: "40px" }}>
        <DesktopActivityCard
          activity={activity}
          activityWithEnrollableTimes={activityWithEnrollableTimes}
          leader={leader}
          isOrganizationEnrollment={isOrganizationEnrollment}
          isLoadingOneOnOneAvailabilities={isLoadingOneOnOneAvailabilities}
          hasOneOnOneAvailabilities={hasOneOnOneAvailabilities}
          onClickViewReviews={onClickViewReviews}
          onClickMoreLikeThis={onClickMoreLikeThis}
          sectionsRef={sectionsRef}
          viewAvailabilityRef={viewAvailabilityRef}
          setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
        />
      </Box>
    </Container>
  );
}
